import { physicianOrderLine } from './paCycles';
import {
  Document, Packer, Paragraph, TextRun,
  Table, TableRow, TableCell,
  WidthType, BorderStyle, ShadingType,
} from 'docx';
import { saveAs } from 'file-saver';
import { LETTERHEAD_SECTION_PROPS, buildHeaders, buildFooters } from './documents/letterhead';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const FONT    = 'Arial';
const FONT_CB = 'MS Gothic';   // checkbox glyphs
const C_NAVY  = '1F3864';
const C_GREY  = '666666';
const C_MID   = '333333';
const C_BORDER = 'B8CCE4';
const C_HDR   = 'D9E2F3';      // light blue for table header rows
const CHECK   = '☑';
const UNCHECK = '☐';

const pt   = (n) => n * 2;    // points → half-points (docx size unit)

// ── BORDERS ───────────────────────────────────────────────────────────────────
const THIN  = { style: BorderStyle.SINGLE, size: 4, color: C_BORDER };
const NONE  = { style: BorderStyle.NONE,   size: 0, color: 'FFFFFF' };
const allNone = { top: NONE, bottom: NONE, left: NONE, right: NONE };
const allThin = { top: THIN, bottom: THIN, left: THIN, right: THIN };

// ── BASE HELPERS ──────────────────────────────────────────────────────────────
const blank = (before = 60, after = 0) =>
  new Paragraph({ children: [new TextRun('')], spacing: { before, after } });

const toArr = (v) => Array.isArray(v) && v.length ? v : [];

// ── TYPOGRAPHY ────────────────────────────────────────────────────────────────
function sectionHeader(text) {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, size: pt(12), color: C_NAVY, font: FONT })],
    spacing: { before: 240, after: 80 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: C_NAVY } },
  });
}

function subHeader(text) {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, size: pt(10), color: C_NAVY, font: FONT })],
    spacing: { before: 160, after: 60 },
  });
}

function subLabel(text) {
  return new Paragraph({
    children: [new TextRun({ text, bold: true, size: pt(9), color: C_NAVY, font: FONT })],
    spacing: { before: 60, after: 40 },
  });
}

// ── MULTI-LINE TEXT ───────────────────────────────────────────────────────────
// Split on newlines and emit one run per line with a real <w:br/> between them.
// A literal "\n" inside a single TextRun lands in the XML as a raw control
// character in <w:t>: Word tolerates it, but Google Docs' importer drops the
// text after it — so every narrative box read as blank once the generated
// .docx was opened from Drive as a Google Doc.
function textRuns(content, opts = {}) {
  return String(content ?? '')
    .split('\n')
    .map((line, i) => new TextRun({ ...opts, text: line, ...(i > 0 ? { break: 1 } : {}) }));
}

// ── TEXT BOX (single-cell bordered table, pre-filled with content) ────────────
function textBox(content) {
  return new Table({
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: [10800],
    rows: [new TableRow({
      children: [new TableCell({
        children: [new Paragraph({
          children: textRuns(content, { size: pt(9), font: FONT }),
          spacing: { before: 60, after: 60 },
        })],
        borders: allThin,
        margins: { top: 80, bottom: 80, left: 120, right: 120 },
      })],
    })],
    borders: { insideH: NONE, insideV: NONE },
  });
}

// ── KV ROWS (two label:value pairs side-by-side, no borders) ─────────────────
function kvRow(pairs) {
  const cells = pairs.map(([label, value]) =>
    new TableCell({
      children: [new Paragraph({
        children: [
          new TextRun({ text: `${label}: `, bold: true, size: pt(9), color: C_NAVY, font: FONT }),
          new TextRun({ text: String(value ?? '—') || '—', size: pt(9), color: value != null && value !== '' ? C_MID : C_GREY, font: FONT }),
        ],
        spacing: { before: 40, after: 40 },
      })],
      borders: allNone,
    })
  );
  while (cells.length < 2) {
    cells.push(new TableCell({ children: [blank(0)], borders: allNone }));
  }
  return new Table({
    rows: [new TableRow({ children: cells.slice(0, 2) })],
    width: { size: 10800, type: WidthType.DXA },
    borders: { top: NONE, bottom: NONE, left: NONE, right: NONE, insideH: NONE, insideV: NONE },
    columnWidths: [5400, 5400],
  });
}

// ── CHECKBOX HELPERS ──────────────────────────────────────────────────────────
function cbLine(allOpts, selected) {
  const sel = new Set(
    Array.isArray(selected)
      ? selected.map(s => String(s).toLowerCase().trim())
      : selected ? [String(selected).toLowerCase().trim()] : []
  );
  const runs = [];
  allOpts.forEach((opt, i) => {
    const on = sel.has(opt.toLowerCase().trim());
    runs.push(new TextRun({ text: on ? CHECK : UNCHECK, size: pt(10), font: FONT_CB }));
    runs.push(new TextRun({
      text: `  ${opt}${i < allOpts.length - 1 ? '    ' : ''}`,
      size: pt(9), font: FONT,
      bold: on,
      color: on ? C_NAVY : C_MID,
    }));
  });
  return new Paragraph({ children: runs, spacing: { before: 0, after: 0 } });
}

function cbGroup(allOpts, selected, perRow = 4) {
  const lines = [];
  for (let i = 0; i < allOpts.length; i += perRow) {
    lines.push(cbLine(allOpts.slice(i, i + perRow), selected));
  }
  return lines;
}

// ── DATA TABLE ────────────────────────────────────────────────────────────────
function dataTable(columns, rows, minRows = 6) {
  const headerRow = new TableRow({
    children: columns.map(col => new TableCell({
      children: [new Paragraph({
        children: [new TextRun({ text: col, bold: true, size: pt(8), color: C_NAVY, font: FONT })],
        spacing: { before: 60, after: 60 },
      })],
      shading: { fill: C_HDR, type: ShadingType.CLEAR, color: 'auto' },
      borders: allThin,
    })),
    tableHeader: true,
  });

  const populated = (rows || []).filter(r => Array.isArray(r) && r.some(c => c && String(c).trim()));
  const padCount  = Math.max(0, minRows - populated.length);
  const allRows   = [...populated, ...Array(padCount).fill(null).map(() => Array(columns.length).fill(''))];

  const colW = Math.floor(10800 / columns.length);
  const colWidths = columns.map((_, i) =>
    i === columns.length - 1 ? 10800 - colW * (columns.length - 1) : colW
  );

  return new Table({
    rows: [headerRow, ...allRows.map(row => new TableRow({
      children: columns.map((_, ci) => new TableCell({
        children: [new Paragraph({
          children: textRuns((row || [])[ci] || '', { size: pt(9), font: FONT }),
          spacing: { before: 80, after: 80 },
        })],
        borders: allThin,
      })),
    }))],
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: colWidths,
  });
}

// ── PATIENT INFO TABLE (5 rows × 6 cols, matches reference layout) ────────────
function patientInfoTable(c, naDate) {
  const dob    = c.date_of_birth
    ? new Date(c.date_of_birth + 'T00:00:00').toLocaleDateString('en-US') : '';
  const ageMs  = c.date_of_birth ? Date.now() - new Date(c.date_of_birth + 'T00:00:00').getTime() : null;
  const age    = ageMs ? `${Math.floor(ageMs / 3.156e10)} yrs` : '';
  const addr   = [c.address, c.city, c.state, c.zip].filter(Boolean).join(', ');
  const pcpStr = [c.doctor_name, c.doctor_practice].filter(Boolean).join(' — ');
  const opStr  = [c.ordering_physician_name, c.ordering_physician_specialty].filter(Boolean).join(' — ');
  const phys   = [pcpStr, opStr].filter(Boolean).join('   |   ') || '—';

  function kc(label, value, span) {
    const props = { borders: allThin };
    if (span) props.columnSpan = span;
    return new TableCell({
      ...props,
      children: [new Paragraph({
        children: [
          new TextRun({ text: `${label}:  `, bold: true, size: pt(8), color: C_NAVY, font: FONT }),
          new TextRun({ text: value || '', size: pt(9), font: FONT, color: value ? C_MID : C_GREY }),
        ],
        spacing: { before: 40, after: 40 },
      })],
    });
  }

  return new Table({
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: [1800, 1800, 1800, 1800, 1800, 1800],
    rows: [
      new TableRow({ children: [
        kc('Child Name', [c.first_name, c.last_name].filter(Boolean).join(' ')),
        kc('DOB', dob),
        kc('Age', age),
        kc('Medicaid ID', c.medicaid_id),
        kc('Gender', c.gender),
        kc('Language', c.language),
      ]}),
      new TableRow({ children: [
        kc('Guardian', [c.guardian_first_name, c.guardian_last_name].filter(Boolean).join(' ')),
        kc('Relationship', c.guardian_relationship),
        kc('Phone', c.guardian_phone),
        kc('Email', c.guardian_email),
        kc('NA Date', naDate || ''),
        kc('County', c.county),
      ]}),
      new TableRow({ children: [
        kc('Address', addr, 4),
        // iep_provided is what the assessment form writes; has_iep is the older
        // intake boolean. Prefer the former, fall back to the latter.
        kc('IEP on File', c.iep_provided || (c.has_iep ? 'Yes' : 'No')),
        kc('Status', c.status),
      ]}),
      new TableRow({ children: [
        kc('Emergency Contact', [c.emergency_first_name, c.emergency_last_name].filter(Boolean).join(' ')),
        kc('Relationship', c.emergency_relationship),
        kc('Phone', c.emergency_phone),
        kc('Alt. Phone', c.emergency_alt_phone),
        kc('Email', c.emergency_email, 2),
      ]}),
      new TableRow({ children: [
        new TableCell({
          columnSpan: 6,
          borders: allThin,
          children: [new Paragraph({
            children: [
              new TextRun({ text: 'Physician(s) — Name / Specialty / Practice:  ', bold: true, size: pt(8), color: C_NAVY, font: FONT }),
              new TextRun({ text: phys, size: pt(9), font: FONT }),
            ],
            spacing: { before: 40, after: 40 },
          })],
        }),
      ]}),
    ],
  });
}

// ── CLINICAL SUMMARY TABLE (allergies / medications / diet / weight) ─────────
// These are collected on the assessment form but previously never printed.
// Sits with the patient header so the numbered sections below keep their
// existing 1.1 – 3.2 numbering (referenced by GAPP reviewers).
function clinicalSummaryTable(c) {
  function kc(label, value, span) {
    const props = { borders: allThin };
    if (span) props.columnSpan = span;
    return new TableCell({
      ...props,
      children: [new Paragraph({
        children: [
          new TextRun({ text: `${label}:  `, bold: true, size: pt(8), color: C_NAVY, font: FONT }),
          ...textRuns(value || '', { size: pt(9), font: FONT, color: value ? C_MID : C_GREY }),
        ],
        spacing: { before: 40, after: 40 },
      })],
    });
  }

  const allergies = c.has_allergies === false && !c.allergies
    ? 'NKDA'
    : (c.allergies || '');

  return new Table({
    width: { size: 10800, type: WidthType.DXA },
    columnWidths: [2700, 8100],
    rows: [
      new TableRow({ children: [
        kc('Weight', c.weight_lbs != null && c.weight_lbs !== '' ? `${c.weight_lbs} lbs` : ''),
        kc('Allergies', allergies),
      ]}),
      new TableRow({ children: [kc('Current Medications', c.current_medications, 2)] }),
      new TableRow({ children: [kc('Diet / Nutrition', c.diet_description, 2)] }),
    ],
  });
}

// ── OPTION LISTS (matching reference exactly) ─────────────────────────────────
const OPTS = {
  equipment:    ['Oxygen', 'Suction', 'Nebulizer', 'CPAP / BiPAP', 'Pulse Oximeter',
                 'G-Tube / J-Tube', 'Feeding Pump', 'Tracheostomy', 'Ventilator', 'Other'],
  feedMethod:   ['Oral', 'G-Tube', 'Pump Feeding', 'J-Tube', 'Bolus Feeding'],
  feedTol:      ['Good', 'Fair', 'Poor'],
  dependency:   ['Independent', 'Requires Assistance', 'Dependent'],
  adlSupport:   ['Feeding', 'Bathing', 'Grooming', 'Toileting', 'Dressing',
                 'Transfers', 'Mobility / Ambulation', 'Communication'],
  respPattern:  ['Normal', 'Labored', 'Retractions', 'Tachypnea', 'Apneic Episodes'],
  airway:       ['Trach Care', 'Suctioning', 'Oxygen', 'Nebulizer', 'Vent Support', 'None'],
  neuro:        ['Cerebral Palsy', 'Seizure Disorder / Epilepsy', 'Traumatic Brain Injury',
                 'Neuromuscular Disorder', 'Global Dev. Delay w/ Neuro Impairment', 'Hydrocephalus (VP Shunt / ETV)'],
  skin:         ['Intact', 'Rash', 'Breakdown', 'Wound / Granulation', 'Stoma'],
  gu:           ['Normal', 'Incontinent', 'Other'],
  behavior:     ['Requires Redirection', 'Wandering Risk', 'Aggression', 'Self-Injury Risk', 'Insufficient Supervision Risk'],
  cgAbility:    ['Independent', 'Needs Training', 'Limited Ability', 'Unable'],
  cgAvail:      ['Full-Time', 'Part-Time', 'Limited', 'Not Available'],
  skilled:      ['Medication Administration', 'Trach Care', 'Suctioning', 'Respiratory Monitoring',
                 'Seizure / Neuro Monitoring', 'Tube Feeding / Pump Mgmnt', 'Wound Care', 'Vital Signs Monitoring',
                 'Infection Surveillance', 'Caregiver Education', 'IV / Line Mgmnt', 'Other'],
  adlUnskilled: ['Feeding', 'Bathing', 'Grooming', 'Toileting', 'Dressing',
                 'Transfers', 'Repositioning', 'Mobility / Walking', 'Meal Preparation', 'Laundry / Cleaning',
                 'School / Transport Assist.', 'Housekeeping', 'Errand Support', 'Supply Management', 'Other'],
  supervision:  ['Safety Supervision', 'One-to-One Supervision', 'Overnight Safety Supervision',
                 'Frequent Redirection for Safety', 'Behavioral Support', 'Supervision – Unsafe Wandering'],
  risks:        ['Airway Compromise', 'Aspiration', 'Seizure', 'Fall', 'Infection',
                 'Behavioral Escalation', 'Insufficient Supervision', 'G-Tube Complication', 'Skin Breakdown', 'Other'],
};

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────
export async function generateAssessmentDoc({ client, assessment, narratives, completedByName, assessmentDate, physicianOrder = null }) {
  const c = client     || {};
  const a = assessment || {};
  const n = narratives || {};

  // Prefer the assessment's own equipment; fall back to the client record only
  // when the assessment has none. (Plain `a.equipment || c.medical_equipment`
  // breaks here because an empty array [] is truthy in JS, so an empty list
  // would wrongly win and blank out all the checkboxes.)
  // Free-text equipment is stored merged into the list as "Other: cough assist",
  // which never matched the plain "Other" checkbox option. Collapse it back so
  // the box actually ticks when other_equipment is filled in.
  const equipment = toArr(
    Array.isArray(a.equipment) && a.equipment.length ? a.equipment : c.medical_equipment
  ).map(e => (/^other\s*:/i.test(String(e)) ? 'Other' : e));

  const children = [

    // ── TITLE (company branding lives in the letterhead header) ─────────────────
    new Paragraph({
      children: [new TextRun({ text: 'Comprehensive Assessment, Care Plan & Caregiver Training', bold: true, size: pt(14), color: C_NAVY, font: FONT })],
      spacing: { before: 0, after: 80 },
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Patient & Caregiver Information', bold: true, size: pt(10), color: C_NAVY, font: FONT })],
      spacing: { before: 40, after: 40 },
    }),
    new Paragraph({
      children: [new TextRun({ text: physicianOrderLine(physicianOrder), italics: true, size: pt(9), color: C_GREY, font: FONT })],
      spacing: { before: 30, after: 60 },
    }),

    // ── PATIENT INFO TABLE ──────────────────────────────────────────────────────
    patientInfoTable(c, assessmentDate),
    blank(60),
    clinicalSummaryTable(c),
    blank(120),

    // ════════════════════════════════════════════════════════════════════════════
    // SECTION 1 — NURSING ASSESSMENT
    // ════════════════════════════════════════════════════════════════════════════
    sectionHeader('SECTION 1 — NURSING ASSESSMENT'),

    subHeader('1.1  Diagnoses'),
    textBox(`Primary Diagnosis (include ICD-10, status & device if applicable):\n${a.primary_diagnosis || ''}`),
    blank(40),
    textBox(`Secondary Diagnoses:\n${a.secondary_diagnoses || 'None'}`),
    blank(40),
    // The assessment row is autosaved every 30s; the client column only updates
    // on an explicit Save, so prefer the assessment's own copy.
    textBox(`Most Recent Hospitalizations (admission date, issue, outcome):\n${a.hospitalizations || c.hospitalizations || ''}`),
    blank(80),

    subHeader('1.2  Medical Equipment & Technology'),
    ...cbGroup(OPTS.equipment, equipment, 5),
    blank(40),
    textBox(`Other Equipment: ${a.other_equipment || c.other_equipment || ''}\n\nRN Clinical Notes – Equipment:\n${n.equipment_notes || a.equipment_notes || ''}`),
    blank(80),

    subHeader('1.3  Feeding Assessment'),
    subLabel('Feeding Method:'),
    cbLine(OPTS.feedMethod, toArr(a.feeding_method)),
    subLabel('Tolerance:'),
    cbLine(OPTS.feedTol, a.feeding_tolerance ? [a.feeding_tolerance] : []),
    blank(40),
    textBox(`RN Feeding Assessment Notes:\n${n.feeding_notes || a.feeding_notes || ''}`),
    blank(80),

    subHeader('1.4  Mobility & ADL Status'),
    subLabel('Dependency Level:'),
    cbLine(OPTS.dependency, a.dependency_level ? [a.dependency_level] : []),
    subLabel('ADLs Requiring Support:'),
    cbLine(OPTS.adlSupport, toArr(a.adl_support_needed)),
    blank(40),
    textBox(`RN Functional Assessment Notes:\n${n.functional_notes || a.functional_notes || ''}`),
    blank(80),

    subHeader('1.5  Respiratory Assessment'),
    subLabel('Respiratory Pattern:'),
    cbLine(OPTS.respPattern, a.respiratory_pattern ? [a.respiratory_pattern] : []),
    subLabel('Airway Needs:'),
    cbLine(OPTS.airway, toArr(a.airway_needs)),
    blank(40),
    textBox(`RN Respiratory Assessment Notes:\n${n.respiratory_notes || a.respiratory_notes || ''}`),
    blank(80),

    subHeader('1.6  Neurological Assessment'),
    ...cbGroup(OPTS.neuro, toArr(a.neuro_diagnoses), 3),
    blank(40),
    textBox(`RN Neurological Notes (include last seizure date if applicable):\n${n.neuro_notes || (c.seizures ? 'Seizure history noted. ' : '') + (a.neuro_notes || '')}`),
    blank(80),

    subHeader('1.7  Skin & GU Assessment'),
    subLabel('Skin Integrity:'),
    cbLine(OPTS.skin, a.skin_integrity ? [a.skin_integrity] : []),
    subLabel('GU Status:'),
    cbLine(OPTS.gu, a.gu_status ? [a.gu_status] : []),
    blank(40),
    textBox(`RN Skin / GU Assessment Notes:\n${n.skin_notes || a.skin_notes || ''}`),
    blank(80),

    subHeader('1.8  Behavioral & Safety Considerations'),
    ...cbGroup(OPTS.behavior, toArr(a.behavioral_flags), 5),
    blank(40),
    textBox(`RN Behavioral & Safety Notes:\n${n.behavioral_notes || a.behavioral_notes || ''}`),
    blank(80),

    subHeader('1.9  Caregiver Status'),
    subLabel('Ability:'),
    cbLine(OPTS.cgAbility, a.caregiver_ability ? [a.caregiver_ability] : []),
    subLabel('Availability:'),
    cbLine(OPTS.cgAvail, a.caregiver_availability ? [a.caregiver_availability] : []),
    blank(40),
    textBox(`Caregiver Notes:\n${n.caregiver_notes || a.caregiver_notes || ''}`),
    blank(80),

    subHeader('1.10  Medical Necessity Summary'),
    textBox(`Medical Necessity Narrative:\n${n.medical_necessity_narrative || a.med_necessity_narrative || ''}`),
    blank(40),
    // Every other narrative box falls back to the saved assessment row; these
    // two did not, so they printed blank whenever the AI text wasn't sitting in
    // React state (i.e. on any reopen-then-download).
    textBox(`Identified Skilled Nursing Needs:\n${n.skilled_hours_justification || a.skilled_hours_justification || a.skilled_needs_summary || ''}`),
    blank(40),
    textBox(`Identified Unskilled (CNA / PSS) Needs:\n${n.unskilled_hours_justification || a.unskilled_hours_justification || a.unskilled_needs_summary || ''}`),
    blank(120),

    // ════════════════════════════════════════════════════════════════════════════
    // SECTION 2 — NURSING CARE PLAN
    // ════════════════════════════════════════════════════════════════════════════
    sectionHeader('SECTION 2 — NURSING CARE PLAN'),

    subHeader('2.1  Skilled Nursing Care Plan'),
    subLabel('Skilled Nursing Interventions (check all that apply):'),
    ...cbGroup(OPTS.skilled, toArr(a.skilled_interventions), 4),
    blank(60),
    subLabel('Skilled Nursing Goals & Interventions:'),
    dataTable(
      ['Clinical Domain', 'Measurable Goal', 'Skilled Intervention & Actions', 'Frequency / Target'],
      (n.skilled_goals_rows?.length ? n.skilled_goals_rows : null) || a.skilled_goals || [], 6
    ),
    blank(120),

    subHeader('2.2  Unskilled Care Plan (CNA / PSS)'),
    subLabel('ADL / IADL Support Required:'),
    ...cbGroup(OPTS.adlUnskilled, toArr(a.adl_unskilled), 5),
    blank(40),
    subLabel('Supervision Required:'),
    ...cbGroup(OPTS.supervision, toArr(a.supervision_needs), 3),
    blank(60),
    subLabel('Unskilled Care Goals & Tasks:'),
    dataTable(
      ['Clinical Domain', 'Measurable Goal', 'Unskilled Tasks & Actions', 'Frequency / Target'],
      (n.unskilled_goals_rows?.length ? n.unskilled_goals_rows : null) || a.unskilled_goals || [], 6
    ),
    blank(120),

    subHeader('2.3  Requested Hours & Justification'),
    kvRow([
      ['RN Skilled Hours / Week', a.skilled_hours_requested != null ? String(a.skilled_hours_requested) : null],
      ['PSS / CNA Hours / Week', a.unskilled_hours_requested != null ? String(a.unskilled_hours_requested) : null],
    ]),
    blank(60),
    textBox(`Hours Justification – Medical Necessity (Skilled & Unskilled):\n${n.hours_justification || a.hours_justification || ''}`),
    blank(120),

    subHeader('2.4  Safety Risk & Monitoring Plan'),
    subLabel('Risks Identified:'),
    ...cbGroup(OPTS.risks, toArr(a.safety_risks), 5),
    blank(60),
    textBox(`Preventive Strategies & Monitoring Plan:\n${n.preventive_strategies || a.preventive_strategies || ''}`),
    blank(60),
    textBox(`Emergency Response Protocol (seizure plan, airway emergency, 911 criteria):\n${n.emergency_protocol || a.emergency_protocol || ''}`),
    blank(160),

    // ════════════════════════════════════════════════════════════════════════════
    // SECTION 3 — CAREGIVER SKILLS ASSESSMENT & TRAINING PLAN
    // ════════════════════════════════════════════════════════════════════════════
    sectionHeader('SECTION 3 — CAREGIVER SKILLS ASSESSMENT & TRAINING PLAN'),
    new Paragraph({
      children: [new TextRun({
        text: "The Provider RN has assessed the primary caregiver's current ability to perform all required care tasks. Training is provided for any identified gaps.",
        italics: true, size: pt(9), color: C_GREY, font: FONT,
      })],
      spacing: { before: 20, after: 50 },
    }),

    subHeader('3.1  Competency Audit'),
    dataTable(
      ['Required Care Task', 'Clinical Risk / Justification', 'Caregiver Status (Able / Needs Training)', 'Status (Completed / Pending)'],
      (n.competency_audit_rows?.length ? n.competency_audit_rows : null) || a.competency_audit || [], 6
    ),
    blank(120),

    subHeader('3.2  Caregiver Teaching Plan'),
    dataTable(
      ['Teaching Goal / Objective', 'Teaching Method & Content', 'Completion Date', 'Status (Completed / Pending)'],
      (n.teaching_plan_rows?.length ? n.teaching_plan_rows : null) || a.teaching_plan || [], 6
    ),
    blank(160),

    // ── SIGNATURE BLOCK ────────────────────────────────────────────────────────
    kvRow([
      ['RN / Case Manager Name', completedByName || '___________________________'],
      ['License #', '___________________________'],
    ]),
    kvRow([
      ['Signature', '___________________________'],
      ['Date Signed', assessmentDate || '___________________________'],
    ]),
    blank(120),
  ];

  const doc = new Document({
    sections: [{
      properties: LETTERHEAD_SECTION_PROPS,
      headers: buildHeaders(),
      footers: buildFooters({
        note: 'AHFC GAPP Unified Assessment v3.0  ·  Valid 1 year from NA Date  ·  Renew 30–60 days prior per GAPP Policy Manual  ·  Georgia GAPP / Medicaid',
      }),
      children,
    }],
    styles: {
      default: { document: { run: { font: FONT, size: pt(9) } } },
    },
  });

  return await Packer.toBlob(doc);
}

export async function downloadAssessmentDoc(params) {
  const { client } = params;
  const clientId = client?.client_id || 'assessment';
  const date     = params.assessmentDate || new Date().toISOString().split('T')[0];
  const filename = `AHFC_GAPP_${clientId}_${date}.docx`;
  const blob     = await generateAssessmentDoc(params);
  saveAs(blob, filename);
  return blob;
}
